import { getBackendUrl } from "@Utils";
import { useTranslation, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useState, useRef, useEffect, useCallback } from "react";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { ImCross } from "react-icons/im";
import RateAnalysis, { type RateAnalysisPayload } from "./RateAnalysis";
import VariableRateModal, { type VariableRateDraft, type VariableRatePayload } from "./VariableRateModal";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import {
  syncItemMeasurementUnit,
  COST_DATABASE_LEVEL_MEASUREMENT_UNIT,
  ESTIMATE_LEVEL_MEASUREMENT_UNIT,
  saveItemRangeUnitValue,
  saveItemCostIndexValue,
  fetchTakeoffsListAttachedToItem,
  getCostDatabaseItem,
  getEstimateItem,
  saveItemAdditionalAmount,
  saveItemDetails,
  saveItemInEstimate,
  getEstimateNumberValueBasedOnSettings,
  getMeasurementUnits,
  isEstimateRequiredPermission,
} from "@Actions/estimation";
import type {
  SaveItemDetailsRequest,
  EstimateData as EstimateDataState,
  EstimateCurrency,
} from "@ServerTypes/estimates/estimates.schema.js";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

import CharmBook from "@Assets/icons/svg/charm_book.svg";

import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import Measurement from "@Features/viewer-toolbar/components/ToolbarViewer/assets/Measurement";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import RelationIcon from "@Features/viewer-toolbar/components/ToolbarViewer/assets/RelationIcon";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import axiosWrapper from "@AxiosWrapper";
import type { GetMeasurementUnitsResponse, SaveItemMeasurementUnitResponse } from "@ServerTypes/units/units.schema";
import type { TakeoffListItemAttached } from "@ServerTypes/takeoffs/takeoffs.schema";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import {
  ADDON_CODE_TO_LAYOUT_FLAG,
  ADDONS_DISPLAY_NAME,
  RATE_ANALYSIS_ADDON_CODE,
  folderPermissions,
} from "@Helpers/constants";
import useLayoutModuleAccess from "@Services/useLayoutModuleAccess";
import type { RootState } from "src/reducers";
import Text from "@CubeComponents/html/Text/Text";

type CurrencyData = EstimateCurrency;

type ItemMeasurementUnit = {
  _id?: string;
  id?: string;
  relation?: string;
  name?: string;
};

type ItemPurchaseUnit = {
  id?: string;
  name?: string;
};

type ItemData = {
  itemId?: ObjectIdString;
  rate?: number | string | null;
  rateAnalysis?: RateAnalysisPayload;
  manualQuantity?: number | string | null;
  quantityFromTakeoffs?: number | null;
  additionalAmount?: number;
  rangeUnitValue?: number;
  costIndex?: number;
  code?: string;
  hasChild?: boolean;
  measurementUnit?: ItemMeasurementUnit;
  purchaseUnit?: ItemPurchaseUnit;
};

/**
 * Blank variable-rate placeholder used before any range unit is selected: `rangeUnit.id` is
 * absent, and VariableRateModal keeps Save disabled until the user picks one.
 */
const emptyVariableRate = (): VariableRateDraft => ({
  isEnabled: false,
  rangeUnit: { name: "" },
  rules: "",
});

type EstimationDetailsProps = {
  closeSidebar: () => void;
  data: ItemData;
  source: string;
  sourceId?: ObjectIdString;
  costDatabaseVersionId?: ObjectIdString;
  fetchCostDatabase?: () => Promise<void>;
  refreshDataGrid: () => void;
  updateItemsListData?: (args: { action: string; data: ItemData }) => void;
  changeEstimateFooterKey?: () => void;
  costDatabaseItemData?: ItemData[];
  rateAnalysisItemsData?: {
    parentRecord?: { code?: string; description?: string };
    checkedRecord?: {
      code?: string;
      description?: string;
      purchaseUnit?: { name?: string };
      purchaseUnitName?: string;
    };
  };
  currencyData: CurrencyData;
  showAnalysisOption?: boolean;
  canEdit?: boolean;
};

function EstimationDetails({
  closeSidebar,
  data,
  source,
  sourceId,
  costDatabaseVersionId: costDatabaseVersionId,
  fetchCostDatabase,
  refreshDataGrid,
  updateItemsListData,
  rateAnalysisItemsData,
  currencyData,
  showAnalysisOption = true,
  canEdit = true,
}: EstimationDetailsProps) {
  const { t } = useTranslation();
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ versionId: ObjectIdString; costDatabaseVersionId: ObjectIdString }>();
  const dispatch = useDispatch();
  const measurementUnitsRef = useRef<HTMLDivElement | null>(null);
  const { RESOURCE_MODIFY } = folderPermissions();
  // Typed, not validated: the sidebar only ever opens for a real BOQ/Cost Database item
  // (callers gate on `_id`), so itemId is always present; the backend route schema still
  // validates it and 400s if malformed.
  const itemId = (data.itemId ?? "") as ObjectIdString;
  const currencySymbol = currencyData.selectedSymbol?.name ?? "";
  const iconColor = "#192048";
  const [variableRate, setVaribleRate] = useState<VariableRateDraft>(emptyVariableRate);
  const rangeUnitName =
    typeof variableRate.rangeUnit === "string" ? variableRate.rangeUnit : (variableRate.rangeUnit?.name ?? "");
  const [takeoffsListAttached, setTakeoffsListAttached] = useState<TakeoffListItemAttached[]>([]);
  const [showVariableRateModal, setShowVariableRateModal] = useState(false);
  const [openRateAnalysisModal, setOpenRateAnalysisModal] = useState(false);
  const [itemProperties, setItemProperties] = useState<{
    _id?: string;
    code?: string;
    description?: string;
    manualQuantity?: number | null;
    quantityFromTakeoffs?: number | null;
    estimateType?: string;
    hasChild?: boolean;
    rate?: number | null;
    purchaseUnit?: ItemPurchaseUnit;
    measurementUnit?: { id?: string; name?: string; type?: string };
    estimateQuantity?: Array<{ amount: number }>;
    amount?: number | null;
    additionalAmount?: number | string | null;
    range?: number | string | null;
    costIndex?: number | string | null;
    parentUpdated?: boolean;
    conversionRateMU?: string;
    conversionRatePU?: string;
    measurementUnitId?: string;
    conversionRate?: string;
  }>({});
  const [isRateEdit, setIsRateEdit] = useState(false);
  const [itemRate, setItemRate] = useState(parseFloat(String(data.rate ?? 0)));
  const [rateAnalysis, setRateAnalysis] = useState(data.rateAnalysis);
  const [manualQuantityData, setManualQuantityData] = useState<{ edit: boolean; value?: number }>({
    edit: false,
  });
  const [actionButtonDisabled, setActionButtonDisabled] = useState(false);
  const addonsAccess = useLayoutModuleAccess(ADDON_CODE_TO_LAYOUT_FLAG[RATE_ANALYSIS_ADDON_CODE]);

  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState & {
    userRole?: number;
  };
  const isPermissionAllowed =
    source == COST_DATABASE_LEVEL_MEASUREMENT_UNIT || isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);

  const showToast = ({ content, type }: { content: TranslatedString; type: string }) => {
    dispatch(showToastNotification({ content, type }));
  };

  const [measurementUnitsOptions, setMeasurementUnitsOptions] = useState<
    NonNullable<GetMeasurementUnitsResponse["result"]>
  >([]);

  const changeItemProperties = async ({
    measurementUnitId,
    conversionRatePU,
    conversionRateMU,
  }: {
    measurementUnitId?: string;
    conversionRatePU: string;
    conversionRateMU: string;
  }) => {
    const fileds = {
      itemId,
      measurementUnitId,
      conversionRate: conversionRatePU.concat(":", conversionRateMU),
      resourceLevel: source,
      resourceId: sourceId,
    };
    const res = await axiosWrapper.post<SaveItemMeasurementUnitResponse>(
      `${getBackendUrl()}/units/saveItemMeasurementUnit`,
      fileds,
    );
    if (source == COST_DATABASE_LEVEL_MEASUREMENT_UNIT) {
      fetchCostDatabase && (await fetchCostDatabase());
      // await fetchVersionsOfCostDatabase();
    }

    if (!res.data.status && source == ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
      showToast({
        content: t("The item is attached to a takeoff. Hence, the selected parameter can not be changed"),
        type: TOAST_TYPE_ERROR,
      });
    }
  };

  const updateMeasurementUnitValue = async (
    _newValue: NonNullable<GetMeasurementUnitsResponse["result"]>[number] | null,
  ) => {
    const measurementUnitId = _newValue?._id;
    const { conversionRateMU, conversionRatePU } = itemProperties;
    if (!measurementUnitId || conversionRateMU === "" || conversionRatePU === "") {
      return;
    }

    await changeItemProperties({
      measurementUnitId,
      conversionRateMU: conversionRateMU ?? "",
      conversionRatePU: conversionRatePU ?? "",
    });

    await fetchItemDetails();
  };

  const syncItemMeasurementUnitSubmit = async () => {
    const response = await syncItemMeasurementUnit({
      itemId,
      estimateId: sourceId,
    });

    if (response.data.status) {
      fetchItemDetails();
      return;
    }

    showToast({
      content: t("The selected unit could not be synced to its parent. Please check if it is attached to a takeoff"),
      type: TOAST_TYPE_ERROR,
    });
  };

  const refreshCallback = async ({ boqListUpdate, data }: { boqListUpdate: boolean; data: ItemData }) => {
    if (boqListUpdate) {
      updateItemsListData && updateItemsListData({ action: "update", data });
    } else if (refreshDataGrid) {
      refreshDataGrid();
    }
    if (source === COST_DATABASE_LEVEL_MEASUREMENT_UNIT && fetchCostDatabase) {
      await fetchCostDatabase();
    }
    await fetchItemDetails();
  };

  const updateAdditionalAmount = async (e: { target: { value: string } }) => {
    if (e.target.value === "" || !sourceId) return;

    const response = await saveItemAdditionalAmount({
      itemId,
      estimateId: sourceId,
      additionalAmount: parseFloat(e.target.value),
    });

    if (response.data.status) {
      await refreshCallback({
        boqListUpdate: !!updateItemsListData,
        data: response.data.result as ItemData,
      });
    }
  };

  const saveItemQuantity = async () => {
    if (!actionButtonDisabled) {
      setActionButtonDisabled(true);
      if (typeof manualQuantityData.value !== "number") {
        setActionButtonDisabled(false);
        return;
      }
      if (manualQuantityData.value < 0) {
        const content = t("Please enter a non negative value for quantity");
        dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
        return;
      }

      if (itemProperties.manualQuantity !== manualQuantityData.value) {
        const updateData = {
          action: "update" as const,
          params: { estimateId: sourceId },
          value: {
            itemId,
            quantity: manualQuantityData.value,
          },
        };

        const response = await saveItemInEstimate(updateData);
        if (response.data.status) {
          setManualQuantityData((prev) => ({ ...prev, edit: false }));
          dispatch(
            showToastNotification({
              content: t("Quantity has been updated successfully!"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          await refreshCallback({
            boqListUpdate: !!updateItemsListData,
            data: response.data.result as ItemData,
          });
        } else {
          dispatch(
            showToastNotification({
              content: untranslated(response.data.errorMessage) || SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      } else {
        setManualQuantityData((prev) => ({ ...prev, edit: false }));
      }

      setActionButtonDisabled(false);
    }
  };

  const updateRate = async (e: { target: { value: string } }) => {
    if (!itemId || !sourceId) return;
    const value = Number(e.target.value.replace(/,/g, ""));
    const updateData: SaveItemDetailsRequest = {
      itemId,
      rate: value,
      sourceId,
      source,
    };

    if (rateAnalysis) {
      const analysisData = rateAnalysis;
      analysisData.rateAnalysisData.say = value || 0;
      updateData.rateAnalysis = analysisData;
    }

    const response = await saveItemDetails(updateData);
    if (response.data.status && response.data.result) {
      setIsRateEdit(false);
      await refreshCallback({
        boqListUpdate: !!updateItemsListData,
        data: response.data.result as ItemData,
      });
    } else {
      showToast({
        content: untranslated(response.data.errorMessage ?? "An error occurred"),
        type: TOAST_TYPE_ERROR,
      });
    }
  };

  const saveRateAnalysisForItem = async (args: { data: RateAnalysisPayload }) => {
    if (!itemId || !sourceId) return;
    const analysisData = args?.data;
    if (!analysisData) {
      return;
    }
    const newRate = analysisData.rateAnalysisData.say;
    const response = await saveItemDetails({
      itemId,
      rate: newRate,
      sourceId,
      source,
      rateAnalysis: analysisData,
      isRateAnalysisEnable: true,
    });

    if (response.data.status && response.data.result) {
      await refreshCallback({
        boqListUpdate: !!updateItemsListData,
        data: response.data.result as ItemData,
      });
      handleCloseRateAnalysisModal();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage ?? "An error occurred"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const updateRangeUnitValue = async (e: { target: { value: string } }) => {
    if (e.target.value === "" || !sourceId) return;

    const response = await saveItemRangeUnitValue({
      itemId,
      estimateId: sourceId,
      rangeUnitValue: parseFloat(e.target.value),
    });

    if (response.data.status) {
      await refreshCallback({
        boqListUpdate: !!updateItemsListData,
        data: response.data.result as ItemData,
      });
    }
  };

  const updateCostIndexValue = async (e: { target: { value: string }; stopPropagation: () => void }) => {
    const costIndex = parseFloat(e.target.value);
    if (costIndex <= 0 || !Number.isFinite(costIndex)) {
      e.stopPropagation();
      showToast({
        content: t("Please enter a positive value for CostIndex."),
        type: TOAST_TYPE_WARNING,
      });
      return;
    }
    if (!sourceId) return;

    const response = await saveItemCostIndexValue({
      itemId,
      estimateId: sourceId,
      costIndex,
    });

    if (response.data.status) {
      await refreshCallback({
        boqListUpdate: !!updateItemsListData,
        data: response.data.result as ItemData,
      });
    }
  };

  const fetchItemDetails = useCallback(async () => {
    if (!itemId || !sourceId) return;
    let itemData;
    if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
      itemData = await getEstimateItem({
        itemId,
        versionId: params.versionId,
        estimateId: sourceId,
      });
    } else {
      itemData = await getCostDatabaseItem({
        itemId,
        costDatabaseVersionId: params.costDatabaseVersionId,
        costDatabaseId: sourceId,
      });
    }
    if (itemData.data.status) {
      const data = itemData.data.data;

      if (data.measurementUnit?.id) {
        const relation = data.measurementUnit.relation.split(":");
        data.conversionRatePU = relation[0];
        data.conversionRateMU = relation[1];
        setVaribleRate((itemData.data.data.variableRate as VariableRatePayload) ?? emptyVariableRate());
      }
      setItemProperties(itemData.data.data);
      setRateAnalysis(itemData.data.data.rateAnalysis);
      setItemRate(parseFloat(String(itemData.data.data.rate ?? 0)));
    }
  }, [itemId, sourceId, source, params.versionId, params.costDatabaseVersionId]);

  const fetchTakeoffsList = useCallback(async () => {
    if (!itemId || !sourceId) return;
    const response = await fetchTakeoffsListAttachedToItem({
      itemId,
      versionId: params.versionId,
      estimateId: sourceId,
    });

    if (response.data.status && response.data.result) {
      setTakeoffsListAttached(response.data.result);
    }
  }, [itemId, sourceId, params.versionId]);

  const openVariableRateModal = () => {
    setShowVariableRateModal(true);
  };

  const closeVariableRateModal = () => {
    setShowVariableRateModal(false);
  };

  const handleFetchData = useCallback(async () => {
    await fetchItemDetails();
  }, [fetchItemDetails]);

  useEffect(() => {
    if (itemProperties._id) {
      setManualQuantityData((prev) => ({
        ...prev,
        value: itemProperties.manualQuantity ?? undefined,
      }));
    }
  }, [itemProperties]);

  useEffect(() => {
    handleFetchData();
  }, [costDatabaseVersionId, handleFetchData]);

  useEffect(() => {
    const fetchMeasurementUnits = async () => {
      const response = await getMeasurementUnits({});
      if (response.data?.result) {
        setMeasurementUnitsOptions(response.data.result);
      }
    };
    fetchMeasurementUnits();
  }, []);

  useEffect(() => {
    if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && !itemProperties.hasChild) {
      fetchTakeoffsList();
    }
  }, [fetchTakeoffsList, itemProperties.hasChild, source]);

  const detailsHeader = () => (
    <div className="flex flex-row items-center justify-start px-2">
      <img alt="" src="/assets/icons/svg/details.svg" className="h-5 w-6 me-1" />
      <strong>
        <Text>{t("Details")}</Text>
      </strong>
    </div>
  );

  const unitsHeader = () => (
    <div className="flex flex-row items-center justify-start px-2">
      <Measurement height={18} color={iconColor} onClick={undefined} />
      <strong className="ms-1">
        <Text>{t("Measurements")}</Text>
      </strong>
    </div>
  );

  const relationHeader = () => (
    <div className="flex flex-row items-center justify-start px-2" data-testid="relationLabel">
      <RelationIcon size={20} />
      <strong className="ms-1">
        <Text>{t("Relation")}</Text>
      </strong>
    </div>
  );

  const takeoffsListHeader = () => (
    <div className="flex flex-row items-center justify-start">
      <img alt="" src={CharmBook} />
      <Text className="font-bold ms-1">{t("Takeoffs Attached")}</Text>
    </div>
  );

  const handleOpenRateAnalysisModal = () => {
    if (source === COST_DATABASE_LEVEL_MEASUREMENT_UNIT && !addonsAccess.allowed) {
      dispatch(
        showToastNotification({
          content: t("addons_access_text_normal", { name: ADDONS_DISPLAY_NAME[RATE_ANALYSIS_ADDON_CODE] }),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      setOpenRateAnalysisModal(true);
    }
  };

  const handleCloseRateAnalysisModal = () => {
    setOpenRateAnalysisModal(false);
  };

  const rateEditClickHandler = () => {
    setIsRateEdit(true);
  };

  const editQuantityClickHandler = () => {
    setManualQuantityData((prev) => ({
      edit: !prev.edit,
      value: itemProperties.manualQuantity ?? undefined,
    }));
  };

  const onQuantityChange = (e: { target: { value: string } }) => {
    setManualQuantityData((prev) => ({
      ...prev,
      value: parseFloat(e.target.value),
    }));
  };

  const itemDetails = () => (
    <div className="px-2 mb-2">
      <div className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="text-[#666a84]">{t("Code")}</Text>
        <p>{itemProperties.code}</p>
      </div>

      <div className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="text-[#666a84]">{t("Description")}</Text>
        <p className="max-w-[70%] break-words">{itemProperties.description}</p>
      </div>

      {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && (
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Manual Quantity")}</Text>
          {manualQuantityData.edit ? (
            <div className="flex gap-2 items-center">
              <NumericTextBoxComponent
                disabled={!canEdit}
                placeholder={t("Enter Quantity")}
                step={1}
                min={0}
                value={manualQuantityData.value}
                onChange={onQuantityChange}
              />
              <IconComponent
                className="cursor-pointer"
                name="reviewSaveIcon"
                onClick={saveItemQuantity}
                isEnabled={!actionButtonDisabled && canEdit}
                width="1.25rem"
                height="1.25rem"
              />
              <IconComponent
                className="cursor-pointer"
                name="close"
                onClick={editQuantityClickHandler}
                isEnabled={!actionButtonDisabled && canEdit}
                width="1rem"
                height="1rem"
              />
            </div>
          ) : (
            <div className=" group flex gap-1 items-center">
              <p>
                {typeof itemProperties.manualQuantity !== "number"
                  ? "-"
                  : getEstimateNumberValueBasedOnSettings(itemProperties.manualQuantity, estimateData, "quantity")}
              </p>
              {!(
                itemProperties.hasChild ||
                (itemProperties.code && (!itemProperties.purchaseUnit?.id || itemProperties.rate === null))
              ) && (
                <IconComponent
                  name="edit"
                  width="1.5rem"
                  height="1.5rem"
                  className={isPermissionAllowed ? "hidden group-hover:flex h-4 w-4 cursor-pointer" : "hidden"}
                  onClick={editQuantityClickHandler}
                  isEnabled={canEdit}
                />
              )}
            </div>
          )}
        </div>
      )}

      {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && itemProperties.estimateType !== "merge" && (
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Quantity from Takeoffs")}</Text>
          <p>
            {typeof itemProperties.quantityFromTakeoffs !== "number"
              ? "-"
              : getEstimateNumberValueBasedOnSettings(itemProperties.quantityFromTakeoffs, estimateData, "takeoff")}
          </p>
        </div>
      )}

      <div className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="text-[#666a84]">{t("Unit")}</Text>
        <p>{itemProperties.purchaseUnit?.name ? itemProperties.purchaseUnit.name : "-"}</p>
      </div>

      <div>
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Rate")}</Text>
          {!isRateEdit && (
            <p className="flex gap-1 items-center">
              {itemRate ? (
                <div className="flex gap-1">
                  <span>{currencyData.selectedSymbol?.name ?? ""}</span>
                  {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT
                    ? getEstimateNumberValueBasedOnSettings(itemRate, estimateData, "rate")
                    : parseFloat(itemRate.toFixed(2))}
                </div>
              ) : (
                "-"
              )}
              {showAnalysisOption &&
                ((source === COST_DATABASE_LEVEL_MEASUREMENT_UNIT && !params.costDatabaseVersionId) ||
                  (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT &&
                    !itemProperties.code &&
                    !params.versionId &&
                    !itemProperties.hasChild &&
                    itemProperties.purchaseUnit?.id &&
                    typeof itemProperties.rate === "number")) && (
                  <IconComponent
                    isEnabled={canEdit}
                    name="edit"
                    width="1.5rem"
                    height="1.5rem"
                    className={
                      source === COST_DATABASE_LEVEL_MEASUREMENT_UNIT || isPermissionAllowed
                        ? "h-4 w-4 cursor-pointer"
                        : "h-4 w-4 cursor-not-allowed opacity-50"
                    }
                    onClick={rateEditClickHandler}
                  />
                )}
            </p>
          )}
          {isRateEdit && (
            <div>
              <NumericTextBoxComponent
                disabled={!canEdit}
                placeholder={t("Enter Rate")}
                value={itemRate}
                onBlur={updateRate}
                // style={{ margin: "0 10px 0 0" }}
              />
            </div>
          )}
        </div>

        {showAnalysisOption &&
        (source !== ESTIMATE_LEVEL_MEASUREMENT_UNIT ||
          (!itemProperties.hasChild && itemProperties.purchaseUnit?.id)) ? (
          <div className="flex flex-row items-center justify-between mb-3 gap-4">
            <Text className="text-[#666a84]">{t("Attach Rate Analysis")}</Text>
            <div className="flex justify-between items-center gap-2.5">
              <span>
                {params.costDatabaseVersionId ||
                (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && (itemProperties.code || params.versionId))
                  ? t("View Rate Analysis")
                  : t("Perform Rate Analysis")}
              </span>
              <IconComponent
                name="openInViewer"
                className="cursor-pointer"
                onClick={handleOpenRateAnalysisModal}
                width="1rem"
                height="1rem"
              />
            </div>
          </div>
        ) : null}

        {source === COST_DATABASE_LEVEL_MEASUREMENT_UNIT &&
          itemProperties.purchaseUnit?.id &&
          typeof itemProperties.rate === "number" &&
          itemProperties.rate && (
            <div className="flex flex-row items-center justify-end">
              <Text className="text-[#666a84]">{t("Variable Rate")}</Text>
              <div className="cursor-pointer">
                <IconComponent name="openInViewer" onClick={openVariableRateModal} width="1.25rem" height="1.25rem" />
              </div>
            </div>
          )}
      </div>

      {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && (
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Amount")}</Text>
          <p>
            {currencySymbol}
            {itemProperties.estimateQuantity && itemProperties.estimateQuantity.length > 1
              ? itemProperties.estimateQuantity.reduce(
                  (accumulator: number, currentValue: { amount: number }) => accumulator + currentValue.amount,
                  0,
                )
              : typeof itemProperties.amount !== "number"
                ? "-"
                : getEstimateNumberValueBasedOnSettings(Number(itemProperties.amount ?? 0), estimateData, "amount")}
          </p>
        </div>
      )}

      {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && (
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Additional Amount")}</Text>
          <p className="flex flex-row items-center justify-start">
            {currencySymbol}
            <TextFieldComponent
              disabled={
                !canEdit || itemProperties.estimateType === "merge" || !!params.versionId || !isPermissionAllowed
              }
              width="11.25rem"
              placeholder={t("Enter Additional Amount")}
              value={itemProperties.additionalAmount !== undefined ? String(itemProperties.additionalAmount) : ""}
              onChange={updateAdditionalAmount}
              sx={{
                margin: "0 0.625rem 0 0 ",
              }}
            />
          </p>
        </div>
      )}

      {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && itemProperties.code && variableRate.isEnabled == true && (
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Range")}</Text>
          <div className="flex flex-row items-center justify-end">
            <TextFieldComponent
              disabled={!!params.versionId || !canEdit}
              width="6.25rem"
              placeholder={t("Enter Range")}
              value={itemProperties.range !== undefined ? String(itemProperties.range) : ""}
              onChange={updateRangeUnitValue}
              sx={{
                margin: "0 0.625rem 0 0 ",
              }}
            />
            <p>{rangeUnitName}</p>
          </div>
        </div>
      )}

      {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && itemProperties.code && !itemProperties.hasChild && (
        <div className="flex flex-row items-center justify-between mb-3 gap-4">
          <Text className="text-[#666a84]">{t("Cost Index")}</Text>
          <div className="flex flex-row items-center justify-end">
            <TextFieldComponent
              disabled={
                itemProperties.estimateType === "merge" || !!params.versionId || !isPermissionAllowed || !canEdit
              }
              width="6.25rem"
              placeholder={t("Enter Cost Index")}
              value={itemProperties.costIndex !== undefined ? String(itemProperties.costIndex) : ""}
              onBlur={updateCostIndexValue}
              sx={{
                margin: "0 0.625rem 0 0 ",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const unitsDetails = () => (
    <div className="flex flex-col px-2 mb-2">
      <label htmlFor="purchaseUnit" data-testid="purchaseUnitLabel">
        <Text>{t("Purchase Unit")}</Text>
      </label>
      <input
        id="purchaseUnit"
        value={itemProperties.purchaseUnit?.name ?? ""}
        className="bg-gray-100 text-gray-500 w-[45%] h-[1.875rem] ps-1 border border-[#c8c8c8] box-border mb-5 cursor-not-allowed"
        disabled
        data-testid="purchaseUnitInput"
      />
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col w-[45%]">
          <label className="flex gap-1 text-center" data-testid="measurementUnitLabel">
            <Text>{t("Measurement Unit")}</Text>

            <IconComponent
              name="details"
              position="right"
              toolTipContent={t(
                "Make sure your Measurement Unit is correct. Only Takeoffs of same Measurement Unit can be attached to this item.",
              )}
              width="1rem"
              height="1rem"
            />
          </label>
          <AutoComplete
            ref={measurementUnitsRef}
            options={measurementUnitsOptions}
            valueField="_id"
            getOptionLabel={(option) => option?.name || ""}
            value={itemProperties.measurementUnit?.id ?? ""}
            className="w-full"
            disabled={
              !(
                itemProperties.estimateType !== "merge" &&
                !params.versionId &&
                !params.costDatabaseVersionId &&
                !takeoffsListAttached?.length &&
                isPermissionAllowed &&
                canEdit
              )
            }
            onChange={(_event, _newValue) => {
              updateMeasurementUnitValue(_newValue);
            }}
            sx={{ width: "100%", marginTop: "0.625rem" }}
            renderInput={(params) => <TextFieldComponent {...params} required />}
          />
        </div>
        <div className="flex flex-col w-[45%]" data-testid="measurementInputContainer">
          <label data-testid="measurementTypeLabel">
            <Text>{t("Measurement Type")}</Text>
          </label>
          <input
            value={itemProperties.measurementUnit?.type ?? ""}
            className="bg-gray-100 text-gray-500 h-[1.875rem] ps-1 border border-[#c8c8c8] box-border cursor-not-allowed"
            disabled
            data-testid="measurementTypeInput"
          />
        </div>
      </div>
    </div>
  );

  const commonRelationDetailsClass = `h-[1.875rem] ps-1 border border-[#c8c8c8] box-border w-[20%] ${
    itemProperties.estimateType === "merge" ||
    !_.get(itemProperties, "measurementUnit.id") ||
    params.versionId ||
    params.costDatabaseVersionId ||
    _.get(takeoffsListAttached, "length")
      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
      : ""
  }`;

  const isDisabledRelationDetails =
    itemProperties.estimateType === "merge" ||
    !_.get(itemProperties, "measurementUnit.id") ||
    params.versionId ||
    params.costDatabaseVersionId ||
    _.get(takeoffsListAttached, "length") ||
    !isPermissionAllowed;

  const relationDetails = () => (
    <div className="flex flex-row items-center justify-between px-2 mb-2">
      <input
        data-testid="itemPropertyInput"
        min="1"
        type="text"
        className={commonRelationDetailsClass}
        value={itemProperties.conversionRatePU}
        onChange={async (e) => {
          setItemProperties((prev) => ({
            ...prev,
            conversionRatePU: e.target.value,
          }));
          const conversionRateMU = itemProperties.conversionRateMU ?? "";
          const measurementUnitId = _.get(itemProperties, "measurementUnit.id");
          if (!measurementUnitId || conversionRateMU === "" || e.target.value === "") {
            return;
          }
          await changeItemProperties({
            measurementUnitId,
            conversionRateMU,
            conversionRatePU: e.target.value,
          });
          await fetchItemDetails();
        }}
        disabled={!!isDisabledRelationDetails || !canEdit}
      />
      <p>{itemProperties.measurementUnit?.name ?? ""}</p>
      <p>=</p>
      <input
        type="text"
        className={commonRelationDetailsClass}
        value={itemProperties.conversionRateMU}
        onChange={async (e) => {
          setItemProperties((prev) => ({
            ...prev,
            conversionRateMU: e.target.value,
          }));
          const conversionRatePU = itemProperties.conversionRatePU ?? "";
          const measurementUnitId = _.get(itemProperties, "measurementUnit.id");
          if (!measurementUnitId || conversionRatePU === "" || e.target.value === "") {
            return;
          }
          await changeItemProperties({
            measurementUnitId,
            conversionRatePU,
            conversionRateMU: e.target.value,
          });
          await fetchItemDetails();
        }}
        disabled={!!isDisabledRelationDetails || !canEdit}
      />
      <p>{itemProperties.purchaseUnit?.name ?? ""}</p>
      {itemProperties.parentUpdated && <p className="w-5 h-5 rounded-full bg-yellow-400" />}
      {itemProperties.code && itemProperties.parentUpdated && (
        <ButtonComponent
          disabled={
            !canEdit ||
            itemProperties.estimateType === "merge" ||
            !!params.versionId ||
            !!params.costDatabaseVersionId ||
            !!_.get(takeoffsListAttached, "length") ||
            !isPermissionAllowed
          }
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={syncItemMeasurementUnitSubmit}
        >
          <Text>{t("Sync")}</Text>
        </ButtonComponent>
      )}
    </div>
  );

  const takeoffsListContent = () => (
    <div>
      {takeoffsListAttached.length ? (
        takeoffsListAttached.map(({ name, description }, index) => (
          <div key={index} className="flex flex-row items-center justify-between">
            <p>{name}</p>
            <p>{description}</p>
          </div>
        ))
      ) : (
        <Text>{t("The item is not attached to any takeoff")}</Text>
      )}
    </div>
  );

  const itemsContent = () => (
    <div className="px-6 pb-2 border-0">
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{detailsHeader()}</AccordionSummary>
        <AccordionDetails>{itemDetails()}</AccordionDetails>
      </Accordion>
    </div>
  );

  const unitsContent = () => (
    <div className="px-6 pb-2 border-0">
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{unitsHeader()}</AccordionSummary>
        <AccordionDetails>{unitsDetails()}</AccordionDetails>
      </Accordion>
    </div>
  );

  const relationContent = () => (
    <div className="px-6 pb-2 border-0">
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{relationHeader()}</AccordionSummary>
        <AccordionDetails>{relationDetails()}</AccordionDetails>
      </Accordion>
    </div>
  );

  const takeOffListContent = () => (
    <div className="px-6 pb-2 border-0">
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{takeoffsListHeader()}</AccordionSummary>
        <AccordionDetails>{takeoffsListContent()}</AccordionDetails>
      </Accordion>
    </div>
  );

  const getSidebarContent = () => (
    <div>
      {/* {source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && uniqueCode && <div className='mb-2 ps-6 flex justify-between'><ButtonComponent disabled={!enableUpgradeBtn} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={upgradeToLatestMarketRateItemVersionClick}>Upgrade to latest marketrate item version </ButtonComponent></div>} */}
      {itemsContent()}
      {itemProperties.purchaseUnit?.id && !itemProperties.hasChild && unitsContent()}
      {itemProperties.purchaseUnit?.id && !itemProperties.hasChild && relationContent()}
      {itemProperties.estimateType != "merge" &&
        source === ESTIMATE_LEVEL_MEASUREMENT_UNIT &&
        !itemProperties.hasChild &&
        takeOffListContent()}
    </div>
  );

  // Typed, not validated: this modal only opens for Cost Database-level items (sourceId is
  // the costDatabaseId then); the backend route schema validates it.
  const costDatabaseIdForVariableRate = (sourceId ?? "") as ObjectIdString;

  return (
    <div className="fixed top-0 start-0 w-screen h-screen bg-[var(--background-scrim)] z-[12]">
      {showVariableRateModal && (
        <VariableRateModal
          variableRate={variableRate}
          setVaribleRate={setVaribleRate}
          costDatabaseId={costDatabaseIdForVariableRate}
          itemId={itemId}
          closeVariableRateModal={closeVariableRateModal}
          costDatabaseVersionId={params.costDatabaseVersionId}
          canEdit={canEdit}
        />
      )}
      <div className="fixed end-[0.25%] top-[0.5%] w-[34.375rem] h-[99%] z-[5] overflow-y-auto bg-white shadow-[0.25rem_0.5rem_1.25rem_0_rgb(0_0_0_/_30%)]">
        <div className="pt-[1.875rem] pb-5 px-5">
          <div className="flex flex-row items-center justify-between">
            <h3
              className="whitespace-nowrap w-[80%] overflow-hidden text-ellipsis text-lg font-bold"
              data-testid="itemPropertyDesc"
            >
              {itemProperties.description}
            </h3>
            <ImCross
              size={12}
              className="cursor-pointer"
              onClick={() => closeSidebar()}
              data-testid="closeSideBarOfItemProperty"
            />
          </div>
        </div>
        <div>{itemProperties._id && getSidebarContent()}</div>
      </div>

      <ModalStateDialogComponent
        isVisible={openRateAnalysisModal}
        allowModalPadding={false}
        headerTitle={
          <div className=" w-full flex justify-between items-center">
            <Text>{t("Rate Analysis")}</Text>
          </div>
        }
        onCancel={handleCloseRateAnalysisModal}
        width="90%"
        modelContent={
          <div className="">
            <RateAnalysis
              rateAnalysisItemsData={rateAnalysisItemsData ?? {}}
              saveRateAnalysisForItem={saveRateAnalysisForItem}
              handleCloseRateAnalysisModal={handleCloseRateAnalysisModal}
              rateAnalysis={rateAnalysis}
              viewMode={
                !canEdit ||
                !!params.costDatabaseVersionId ||
                (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT && !!(itemProperties.code || params.versionId))
              }
              source={source}
              currencyData={currencyData}
            />
          </div>
        }
      />
    </div>
  );
}

export default EstimationDetails;
